import { getStoredLocation } from "./locationStorage";
import SnackbarUtils from "./snackbarUtils";

interface ApiClientConfig {
    baseURL: string;
    tokenKey: string;
    loginPath: string;
    silent?: boolean;
}

export interface CustomHeaders extends Record<string, string | boolean | undefined> {
    _silent?: boolean;
    Accept?: string;
    "Content-Type"?: string;
    "X-Country-Code"?: string;
    "X-State"?: string;
    "X-City"?: string;
    Authorization?: string;
}

export interface ApiResponse<T = unknown> {
    success?: boolean;
    data?: T;
    message?: string;
    error?: string;
    errors?: Record<string, string[]>;
    access_token?: string;
    [key: string]: unknown;
}

class ApiClient {
    baseURL: string;
    tokenKey: string;
    loginPath: string;
    silent: boolean;
    isRedirecting: boolean;

    constructor({ baseURL, tokenKey, loginPath, silent = false }: ApiClientConfig) {
        this.baseURL = baseURL;
        this.tokenKey = tokenKey;
        this.loginPath = loginPath;
        this.silent = silent;
        this.isRedirecting = false;
    }

    async request<T = unknown>(method: string, uri: string, body: unknown = null, customHeaders: CustomHeaders = {}): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${uri}`;
        const token = localStorage.getItem(this.tokenKey);

        // Check for silent override in headers
        const silentRequest = customHeaders._silent !== undefined ? customHeaders._silent : this.silent;
        // Create a copy of headers without the internal _silent flag
        const actualHeaders = { ...customHeaders };
        delete actualHeaders._silent;

        const isFormData = body instanceof FormData;
        const headers: CustomHeaders = {
            Accept: "application/json",
            ...actualHeaders,
        };

        // Method tunneling for CORS bypass (PUT/PATCH -> POST with _method)
        let effectiveMethod = method;
        let effectiveBody = body;

        if (method === "PUT" || method === "PATCH") {
            effectiveMethod = "POST";
            if (isFormData) {
                (body as FormData).append("_method", method);
            } else {
                effectiveBody = {
                    ...(body as object || {}),
                    _method: method,
                };
            }
        }

        if (!isFormData && !headers["Content-Type"]) {
            headers["Content-Type"] = "application/json";
        }

        if (isFormData && headers["Content-Type"]) {
            delete headers["Content-Type"];
        }

        const { countryCode, state, city } = getStoredLocation();
        if (countryCode) headers["X-Country-Code"] = countryCode;
        if (state) headers["X-State"] = state;
        if (city) headers["X-City"] = city;

        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        const options: RequestInit = {
            method: effectiveMethod,
            headers: headers as HeadersInit,
            body: effectiveBody ? (isFormData ? effectiveBody as FormData : JSON.stringify(effectiveBody)) : null,
        };

        try {
            const response = await fetch(url, options);

            // Handle 204 No Content or empty responses if necessary, though API seems to return JSON
            const res = await response.json().catch(() => ({ success: false })) as ApiResponse<T>;

            if (response.ok || response.status === 201) {
                // Handle access token refresh if present
                if (res.access_token) {
                    localStorage.setItem(this.tokenKey, res.access_token);
                }

                // Display success message if not silent and message exists
                if (!silentRequest) {
                    const data = res.data as Record<string, unknown> | undefined;
                    if (data && typeof data.message === 'string') {
                        SnackbarUtils.success(data.message);
                    } else if (typeof res.message === 'string') {
                        SnackbarUtils.success(res.message);
                    }
                }

                return res;
            } else {
                // Handle 401 Unauthorized
                if (response.status === 401) {
                    this.handleUnauthorized(Boolean(silentRequest));
                    return res; // Return response even on 401
                }

                // Handle 403 Forbidden
                if (response.status === 403) {
                    localStorage.removeItem(this.tokenKey);
                    globalThis.location.href = this.loginPath;
                    return res;
                }

                const resData = res?.data as Record<string, unknown> | undefined;
                const errors = res?.errors as Record<string, string[]> | undefined;

                const errorMessage =
                    (resData?.error as string) ||
                    (res?.error as string) ||
                    (errors ? Object.values(errors).flat()[0] : null) ||
                    (res?.message as string) ||
                    "An error occurred";

                // Display error if not silent
                if (!silentRequest) {
                    SnackbarUtils.error(errorMessage);
                }

                throw new Error(errorMessage);
            }
        } catch (err) {
            const error = err as Error;
            // If it's a network error (fetch failed) and not silent, we might want to show it
            // But we need to distinguish between the errors we threw above (which already showed snackbar)
            // and unexpected errors.
            // For now, we'll rely on the caller or the fact that we already showed snackbar for API errors.
            if (!silentRequest && error.name === 'TypeError' && error.message === 'Failed to fetch') {
                SnackbarUtils.error("Network error. Please check your connection.");
            }
            throw error;
        }
    }

    handleUnauthorized(silentRequest = false) {
        const currentPath = globalThis?.location?.pathname || "";
        const isProtectedRoute = /^\/dashboard(?:\/|$)/.test(currentPath) || /^\/admin(?:\/|$)/.test(currentPath);

        // Public routes should not be forced to sign in by background API calls.
        // We still clear stale auth so protected pages can recover correctly.
        if (!isProtectedRoute) {
            localStorage.removeItem(this.tokenKey);
            return;
        }

        // Prevent cross-context redirects: the user API client should not redirect
        // when on an admin route, and vice versa. Only redirect if the current path
        // belongs to this client's context.
        const isAdminClient = this.loginPath === '/admin-login';
        const isOnAdminRoute = /^\/admin(?:\/|$)/.test(currentPath);
        const isOnUserRoute = /^\/dashboard(?:\/|$)/.test(currentPath);

        if (isAdminClient && !isOnAdminRoute) {
            return; // Admin client 401 on a non-admin page — ignore
        }
        if (!isAdminClient && !isOnUserRoute) {
            return; // User client 401 on a non-user page — ignore
        }

        if (!this.isRedirecting) {
            this.isRedirecting = true;
            localStorage.removeItem(this.tokenKey);

            if (globalThis.location.pathname !== this.loginPath) {
                if (!silentRequest) {
                    SnackbarUtils.error("Session expired. Redirecting...");
                }
                globalThis.location.href = this.loginPath;
            } else if (!silentRequest) {
                SnackbarUtils.error("Please check your account details.");
            }

            setTimeout(() => {
                this.isRedirecting = false;
            }, 5000);
        }
    }
}

export default ApiClient;
